// Inline analytics snippet builders, moved verbatim from the retired
// hardcoded loader components (VenueGoogleAnalyticsLoader,
// VenueGoogleTagLoader, VenueMetaPixelLoader). Behavior-preservation is
// proven by the golden-string equivalence test in
// ../client/components/__tests__/VenueAnalyticsScripts.test.tsx — do not
// reformat these template literals.

export type Ga4SnippetEntry = {
  id: string;
  /** `false` = reader-only side-by-side instance, used when a GTM container
   * on the same page already loads this same measurement ID (otherwise
   * pageviews would be counted twice). */
  sendPageView: boolean;
  /** Domains the GA4 linker should auto-decorate `_gl` on. Must be identical
   * on every hop. `accept_incoming: true` (set per-config below) is the other
   * half of the cross-domain handshake — without it the destination doesn't
   * read incoming `_gl` even if the origin sends it. */
  linkerDomains: string[];
};

export const buildGa4Snippet = (entries: Ga4SnippetEntry[]): string => {
  const configs = entries
    .map((e) =>
      e.linkerDomains.length > 0
        ? `
gtag('config', '${e.id}', {
  send_page_view: ${e.sendPageView},
  linker: { domains: ${JSON.stringify(e.linkerDomains)}, accept_incoming: true }
});`
        : `
gtag('config', '${e.id}', {
  send_page_view: ${e.sendPageView}
});`
    )
    .join("");
  // Publish the loaded measurement IDs so client components without venue
  // context (e.g. TixologiWidgetIframe in the global Modals container) can
  // resolve gtag('get', id, 'client_id') for cross-domain identity handoff.
  // See docs/specs/tixologi-widget.md → "GA4 cross-domain handoff".
  const publishIds = `window.__punchupVenueGa4Ids = ${JSON.stringify(
    entries.map((e) => e.id)
  )};`;
  return `
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());${configs}
${publishIds}
`;
};

export const buildGtmSnippet = (gtmId: string): string => {
  return `
(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');
`;
};

const META_PIXEL_BOOTSTRAP = `!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');`;

export const buildMetaPixelSnippet = (pixelId: string): string => {
  return `
${META_PIXEL_BOOTSTRAP}
fbq('init', '${pixelId}');
fbq('track', 'PageView');
`;
};

/** Inits each platform pixel once per page load, whichever snippet runs
 * first: the apps/web root layout and the venue loader both render the
 * Punchup pixel, and `window.__punchupMetaPixelIds` keeps it to one
 * init + one PageView. `trackSingle` so a PageView never fans out to a
 * venue's own pixel. */
export const buildPlatformMetaPixelSnippet = (
  pixelIds: readonly string[]
): string => {
  return `
${META_PIXEL_BOOTSTRAP}
window.__punchupMetaPixelIds = window.__punchupMetaPixelIds || {};
${JSON.stringify(pixelIds)}.forEach(function (id) {
  if (window.__punchupMetaPixelIds[id]) return;
  window.__punchupMetaPixelIds[id] = true;
  fbq('init', id);
  fbq('trackSingle', id, 'PageView');
});
`;
};

export const buildTikTokPixelSnippet = (pixelId: string): string => {
  return `
!function (w, d, t) {
  w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script");n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
  ttq.load('${pixelId}');
  ttq.page();
}(window, document, 'ttq');
`;
};
